import React, { useMemo, useState, useEffect, useRef } from 'react';
import {
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  Message,
  Space,
  Alert,
  Upload,
  Card,
  Tag,
  Progress
} from '@arco-design/web-react';
import {
  IconUser,
  IconFile,
  IconUpload,
  IconCheck,
  IconClose
} from '@arco-design/web-react/icon';
import { useTranslation } from 'react-i18next';
import { useSaveIdentity, useUploadDocument, useDeleteDocument } from '../hooks/useOnboardingData';
import { getApiUrl } from '@/config/api';
import './IdentityStep.css';
import './DocumentUploadCard.css';
import DocumentPreviewModal from './DocumentPreviewModal';
import ReportProblemButton from '../components/ReportProblemButton';

const FormItem = Form.Item;
const { Option } = Select;
const PRIMARY_COUNTRY_CODES = [
  { value: '+52', label: 'México' },
  { value: '+1', label: 'USA' },
  { value: '+57', label: 'Colombia' },
  { value: '+56', label: 'Chile' },
  { value: '+34', label: 'España' }
];
const LATAM_COUNTRY_CODES = [
  { value: '+51', label: 'Perú' },
  { value: '+52', label: 'México' },
  { value: '+53', label: 'Cuba' },
  { value: '+54', label: 'Argentina' },
  { value: '+55', label: 'Brasil' },
  { value: '+56', label: 'Chile' },
  { value: '+57', label: 'Colombia' },
  { value: '+58', label: 'Venezuela' },
  { value: '+501', label: 'Belice' },
  { value: '+502', label: 'Guatemala' },
  { value: '+503', label: 'El Salvador' },
  { value: '+504', label: 'Honduras' },
  { value: '+505', label: 'Nicaragua' },
  { value: '+506', label: 'Costa Rica' },
  { value: '+507', label: 'Panamá' },
  { value: '+509', label: 'Haití' },
  { value: '+591', label: 'Bolivia' },
  { value: '+592', label: 'Guyana' },
  { value: '+593', label: 'Ecuador' },
  { value: '+594', label: 'Guayana Francesa' },
  { value: '+595', label: 'Paraguay' },
  { value: '+597', label: 'Surinam' },
  { value: '+598', label: 'Uruguay' }
];
const PRIMARY_COUNTRY_CODE_VALUES = new Set(
  PRIMARY_COUNTRY_CODES.map((item) => item.value)
);
const parseCountryCodeValue = (value) => {
  const numeric = parseInt(String(value || '').replace('+', ''), 10);
  return Number.isNaN(numeric) ? 0 : numeric;
};
const EXTRA_LATAM_COUNTRY_CODES = [...LATAM_COUNTRY_CODES]
  .sort((a, b) => parseCountryCodeValue(a.value) - parseCountryCodeValue(b.value))
  .filter((item) => !PRIMARY_COUNTRY_CODE_VALUES.has(item.value));

function CorporateIdentityStep({ onNext, initialData, documents: initialDocuments, requestedChanges, previewMode = false, freeMode = false }) {
  const { t } = useTranslation('onboarding');
  const [form] = Form.useForm();
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [governmentIdType, setGovernmentIdType] = useState('CURP');
  const [showAllCountryCodes, setShowAllCountryCodes] = useState(false);
  const [documents, setDocuments] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewTitle, setPreviewTitle] = useState('');
  const [previewVisible, setPreviewVisible] = useState(false);
  const isHydrating = useRef(false);
  const autoSaveRef = useRef(null);

  const saveMutation = useSaveIdentity();
  const uploadMutation = useUploadDocument();
  const deleteMutation = useDeleteDocument();

  const changeMap = useMemo(() => {
    const map = {};
    (requestedChanges || [])
      .filter((change) => change.step === 'identity' && !change.resolved)
      .forEach((change) => {
        map[change.field] = change.message;
      });
    return map;
  }, [requestedChanges]);
  const documentChangeMap = useMemo(() => {
    const map = {};
    (requestedChanges || [])
      .filter((change) => change.step === 'documents' && !change.resolved)
      .forEach((change) => {
        map[change.field] = change.message;
      });
    return map;
  }, [requestedChanges]);
  const countryCodeOptions = useMemo(() => {
    return showAllCountryCodes
      ? [...PRIMARY_COUNTRY_CODES, ...EXTRA_LATAM_COUNTRY_CODES]
      : PRIMARY_COUNTRY_CODES;
  }, [showAllCountryCodes]);
  const renderCountryCodeDropdown = (menu) => (
    <div>
      {menu}
      <div className="phone-prefix-footer" onMouseDown={(event) => event.preventDefault()}>
        <Button
          type="text"
          size="mini"
          onClick={() => setShowAllCountryCodes((prev) => !prev)}
        >
          {showAllCountryCodes ? t('steps.identity.options.showLessCountries') : t('steps.identity.options.showMoreCountries')}
        </Button>
      </div>
    </div>
  );

  const getFieldProps = (field) => {
    const message = changeMap[field];
    if (!message) return {};
    return {
      validateStatus: 'error',
      help: message
    };
  };

  useEffect(() => {
    autoSaveRef.current = setInterval(() => {
      const values = form.getFieldsValue();
      const hasData = Object.values(values).some(v => v !== undefined && v !== null && v !== '');
      if (hasData && !saveMutation.isPending && !previewMode) {
        const payload = normalizeIdentityPayload(values);
        saveMutation.mutate(payload);
      }
    }, 30000);

    return () => {
      clearInterval(autoSaveRef.current);
    };
  }, []);

  useEffect(() => {
    if (initialData) {
      isHydrating.current = true;
      form.setFieldsValue({
        first_name: initialData.first_name || '',
        middle_name: initialData.middle_name || '',
        last_name: initialData.last_name || '',
        second_last_name: initialData.second_last_name || '',
        date_of_birth: initialData.date_of_birth || null,
        contact_email: initialData.contact_email || '',
        phone_country_code: initialData.phone_country_code || '+52',
        phone: initialData.phone || '',
        government_id: initialData.government_id || '',
        government_id_type: initialData.government_id_type || 'CURP',
        government_id_type_other: initialData.government_id_type_other || ''
      });

      setGovernmentIdType(initialData.government_id_type || 'CURP');
      setTimeout(() => {
        isHydrating.current = false;
      }, 0);
    }
  }, [initialData, form]);

  useEffect(() => {
    setDocuments(initialDocuments || {});
  }, [initialDocuments]);

  const handleValuesChange = (changedValues) => {
    if (isHydrating.current) {
      return;
    }
    if (Object.prototype.hasOwnProperty.call(changedValues, 'government_id_type')) {
      const nextType = changedValues.government_id_type || 'CURP';
      setGovernmentIdType(nextType);
      if (nextType !== 'OTHER') {
        form.setFieldsValue({ government_id_type_other: '' });
      }
    }
  };

  const handleUpload = async (file, documentKey) => {
    if (previewMode) {
      setDocuments(prev => ({
        ...prev,
        [documentKey]: {
          file_name: file?.name || t('shared.labels.document'),
          file_url: '',
          file_path: '',
          file_type: file?.type || ''
        }
      }));
      Message.success(t('shared.messages.simulationDocUploaded'));
      return false;
    }

    setUploadProgress(prev => ({ ...prev, [documentKey]: 0 }));

    uploadMutation.mutate({ file, documentType: documentKey }, {
      onSuccess: (response) => {
        const uploadedDocument = response?.data?.document;
        if (!uploadedDocument) {
          Message.error(t('shared.messages.documentNoInfo'));
          setUploadProgress(prev => {
            const next = { ...prev };
            delete next[documentKey];
            return next;
          });
          return;
        }

        setDocuments(prev => ({
          ...prev,
          [documentKey]: uploadedDocument
        }));

        Message.success(t('shared.messages.documentUploaded'));

        setTimeout(() => {
          setUploadProgress(prev => {
            const next = { ...prev };
            delete next[documentKey];
            return next;
          });
        }, 800);
      },
      onError: (error) => {
        console.error('Error al subir documento:', error);
        Message.error(t('shared.messages.documentUploadError'));
        setUploadProgress(prev => {
          const next = { ...prev };
          delete next[documentKey];
          return next;
        });
      }
    });

    return false;
  };

  const handleDelete = async (documentKey) => {
    if (previewMode) {
      setDocuments(prev => {
        const next = { ...prev };
        delete next[documentKey];
        return next;
      });
      Message.success(t('shared.messages.simulationDocDeleted'));
      return;
    }

    deleteMutation.mutate(documentKey, {
      onSuccess: () => {
        setDocuments(prev => {
          const next = { ...prev };
          delete next[documentKey];
          return next;
        });
        Message.success(t('shared.messages.documentDeleted'));
      },
      onError: (error) => {
        console.error('Error al eliminar documento:', error);
        Message.error(t('shared.messages.documentDeleteError'));
      }
    });
  };

  const buildFileUrl = (fileUrl) => {
    if (!fileUrl) return '';
    if (fileUrl.startsWith('http')) return fileUrl;
    return `${getApiUrl()}${fileUrl}`;
  };

  const getFileExtension = (fileName = '') => {
    const parts = fileName.split('.');
    return parts.length > 1 ? parts.pop().toLowerCase() : '';
  };

  const renderDocumentThumbnail = (doc) => {
    const fileUrl = buildFileUrl(doc?.file_url || doc?.file_path || '');
    const fileName = doc?.file_name || t('shared.labels.document');
    const extension = getFileExtension(fileName);
    const isPdf = extension === 'pdf';
    const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension);
    const extensionLabel = extension ? extension.toUpperCase() : 'Archivo';

    if (!fileUrl) {
      return (
        <div className="document-thumbnail-fallback">
          <IconFile />
          <span>{t('shared.labels.noPreview')}</span>
        </div>
      );
    }

    if (isImage) {
      return (
        <img
          src={fileUrl}
          alt={fileName}
          className="document-thumbnail-image"
          loading="lazy"
        />
      );
    }

    if (isPdf) {
      return (
        <object data={fileUrl} type="application/pdf" className="document-thumbnail-frame">
          <div className="document-thumbnail-fallback">
            <IconFile />
            <span>PDF</span>
          </div>
        </object>
      );
    }

    return (
      <div className="document-thumbnail-fallback">
        <IconFile />
        <span>{extensionLabel}</span>
      </div>
    );
  };

  const openPreview = (doc, title) => {
    if (!doc) return;
    setPreviewDoc(doc);
    setPreviewTitle(title || doc.file_name || t('shared.labels.document'));
    setPreviewVisible(true);
  };

  const closePreview = () => {
    setPreviewVisible(false);
    setPreviewDoc(null);
    setPreviewTitle('');
  };

  const renderIdDocumentCard = () => {
    const documentKey = 'representative_id';
    const isUploaded = !!documents[documentKey];
    const progress = uploadProgress[documentKey];
    const changeMessage = documentChangeMap[documentKey];
    const previewLabel = t('steps.corporateIdentity.labels.representativeId');
    const description = t('steps.corporateIdentity.documents.representativeIdDesc');

    return (
      <Card className={`document-upload-card${changeMessage ? ' document-upload-card--change' : ''}`} bordered>
        <div className="document-card-header">
          <div className="document-title">
            <IconFile style={{ marginRight: 8 }} />
            <span>{t('steps.corporateIdentity.labels.representativeId')}</span>
            <Tag color="red" size="small" style={{ marginLeft: 8 }}>{t('shared.labels.required')}</Tag>
          </div>
          {isUploaded && (
            <Tag color="green" icon={<IconCheck />}>
              {t('shared.labels.uploaded')}
            </Tag>
          )}
        </div>

        <div className="document-description">
          <small>{description}</small>
        </div>
        {changeMessage && (
          <div className="document-change-note">{changeMessage}</div>
        )}

        {progress !== undefined && (
          <Progress
            percent={progress}
            status={progress === 100 ? 'success' : 'normal'}
            style={{ marginTop: 12 }}
          />
        )}

        {!isUploaded && progress === undefined && (
          <Upload
            accept=".pdf,.jpg,.jpeg,.png"
            beforeUpload={(file) => handleUpload(file, documentKey)}
            showUploadList={false}
          >
            <Button
              type="outline"
              icon={<IconUpload />}
              style={{ marginTop: 12, width: '100%' }}
              loading={uploadMutation.isPending && uploadMutation.variables?.documentType === documentKey}
            >
              {t('shared.buttons.selectFile')}
            </Button>
          </Upload>
        )}

        {isUploaded && (
          <div className="document-actions">
            <div className="document-info">
              <button
                type="button"
                className="document-thumbnail"
                onClick={() => openPreview(documents[documentKey], previewLabel)}
                aria-label={t('shared.labels.viewDocument')}
              >
                {renderDocumentThumbnail(documents[documentKey])}
              </button>
              <div className="document-meta">
                <small>{documents[documentKey].file_name}</small>
                <small>{(documents[documentKey].file_size / 1024).toFixed(1)} KB</small>
              </div>
            </div>
            <Button
              type="text"
              status="danger"
              icon={<IconClose />}
              onClick={() => handleDelete(documentKey)}
            >
              {t('shared.labels.delete')}
            </Button>
          </div>
        )}

        <div className="document-help">
          <small>{t('shared.labels.fileFormats')}</small>
        </div>
      </Card>
    );
  };

  const renderPowerDocumentCard = () => {
    const documentKey = 'power_of_attorney';
    const isUploaded = !!documents[documentKey];
    const progress = uploadProgress[documentKey];
    const changeMessage = documentChangeMap[documentKey];
    const previewLabel = t('steps.corporateIdentity.labels.powerOfAttorney');

    return (
      <Card className={`document-upload-card${changeMessage ? ' document-upload-card--change' : ''}`} bordered>
        <div className="document-card-header">
          <div className="document-title">
            <IconFile style={{ marginRight: 8 }} />
            <span>{t('steps.corporateIdentity.labels.powerOfAttorney')}</span>
            <Tag color="red" size="small" style={{ marginLeft: 8 }}>{t('shared.labels.required')}</Tag>
          </div>
          {isUploaded && (
            <Tag color="green" icon={<IconCheck />}>
              {t('shared.labels.uploaded')}
            </Tag>
          )}
        </div>

        <div className="document-description">
          <small>{t('steps.corporateIdentity.documents.powerOfAttorneyDesc')}</small>
        </div>
        {changeMessage && (
          <div className="document-change-note">{changeMessage}</div>
        )}

        {progress !== undefined && (
          <Progress
            percent={progress}
            status={progress === 100 ? 'success' : 'normal'}
            style={{ marginTop: 12 }}
          />
        )}

        {!isUploaded && progress === undefined && (
          <Upload
            accept=".pdf,.jpg,.jpeg,.png"
            beforeUpload={(file) => handleUpload(file, documentKey)}
            showUploadList={false}
          >
            <Button
              type="outline"
              icon={<IconUpload />}
              style={{ marginTop: 12, width: '100%' }}
              loading={uploadMutation.isPending && uploadMutation.variables?.documentType === documentKey}
            >
              {t('shared.buttons.selectFile')}
            </Button>
          </Upload>
        )}

        {isUploaded && (
          <div className="document-actions">
            <div className="document-info">
              <button
                type="button"
                className="document-thumbnail"
                onClick={() => openPreview(documents[documentKey], previewLabel)}
                aria-label={t('shared.labels.viewDocument')}
              >
                {renderDocumentThumbnail(documents[documentKey])}
              </button>
              <div className="document-meta">
                <small>{documents[documentKey].file_name}</small>
                <small>{(documents[documentKey].file_size / 1024).toFixed(1)} KB</small>
              </div>
            </div>
            <Button
              type="text"
              status="danger"
              icon={<IconClose />}
              onClick={() => handleDelete(documentKey)}
            >
              {t('shared.labels.delete')}
            </Button>
          </div>
        )}

        <div className="document-help">
          <small>{t('shared.labels.fileFormats')}</small>
        </div>
      </Card>
    );
  };

  const normalizeIdentityPayload = (values) => {
    const payload = { ...values };
    if (payload.date_of_birth && typeof payload.date_of_birth === 'string') {
      const parts = payload.date_of_birth.split('/');
      if (parts.length === 3) {
        payload.date_of_birth = `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
    }

    return payload;
  };

  const validateCURP = (value) => {
    if (!value) return true;

    const curpRegex = /^[A-Z]{4}[0-9]{6}[HM][A-Z]{5}[0-9A-Z][0-9]$/;

    if (!curpRegex.test(value)) {
      return false;
    }

    return true;
  };

  const validateAge = (date) => {
    if (!date) return true;

    const today = new Date();
    const birthDate = new Date(date);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age >= 18;
  };

  const handleSubmit = async (values) => {
    setDuplicateWarning(null);

    if (previewMode) {
      Message.success(t('shared.messages.simulationIdentityValidated'));
      onNext(null);
      return;
    }

    const payload = normalizeIdentityPayload(values);
    saveMutation.mutate(payload, {
      onSuccess: (response) => {
        const data = response?.data || {};
        if (data.duplicate_identity_found) {
          setDuplicateWarning(data.duplicate_identity_found?.message || null);
        }
        Message.success(t('shared.messages.identitySaved'));
        onNext(null);
      },
      onError: (error) => {
        console.error('Error al guardar identidad:', error);
        Message.error(t('shared.messages.identityError'));
      }
    });
  };

  const handleSaveDraft = async () => {
    setDuplicateWarning(null);

    if (previewMode) {
      Message.success(t('shared.messages.simulationDraftSaved'));
      return;
    }

    const values = form.getFieldsValue();
    const payload = normalizeIdentityPayload(values);
    saveMutation.mutate(payload, {
      onSuccess: (response) => {
        const data = response?.data || {};
        if (data.duplicate_identity_found) {
          setDuplicateWarning(data.duplicate_identity_found?.message || null);
        }
        Message.success(t('shared.messages.identitySavedDraft'));
      },
      onError: (error) => {
        console.error('Error al guardar borrador:', error);
        Message.error(t('shared.messages.errorSavingDraft'));
      }
    });
  };

  const handleResetForm = () => {
    form.resetFields();
    setGovernmentIdType('CURP');
  };

  return (
    <div className="identity-step">
      <div className="step-header">
        <h2>{t('steps.corporateIdentity.title')}</h2>
        <p>{t('steps.corporateIdentity.description')}</p>
      </div>

      {duplicateWarning && (
        <Alert
          type="warning"
          content={duplicateWarning}
          closable
          onClose={() => setDuplicateWarning(null)}
          style={{ marginBottom: 20 }}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        onSubmit={handleSubmit}
        onValuesChange={handleValuesChange}
        autoComplete="off"
      >
        {/* Datos Personales */}
        <div className="form-section">
          <h3>
            <IconUser style={{ marginRight: 8 }} />
            {t('steps.corporateIdentity.labels.personalInfo')}
          </h3>

          <div className="form-row">
            <FormItem
              label={t('steps.corporateIdentity.labels.firstName')}
              field="first_name"
              rules={[
                { required: true, message: t('steps.corporateIdentity.validation.nameRequired') },
                { minLength: 2, message: t('steps.corporateIdentity.validation.minTwoChars') }
              ]}
              {...getFieldProps('first_name')}
            >
              <Input placeholder={t('steps.corporateIdentity.placeholders.firstName')} />
            </FormItem>

            <FormItem
              label={t('steps.corporateIdentity.labels.middleName')}
              field="middle_name"
              {...getFieldProps('middle_name')}
            >
              <Input placeholder={t('steps.corporateIdentity.placeholders.middleName')} />
            </FormItem>
          </div>

          <div className="form-row">
            <FormItem
              label={t('steps.corporateIdentity.labels.lastName')}
              field="last_name"
              rules={[
                { required: true, message: t('steps.corporateIdentity.validation.lastNameRequired') }
              ]}
              {...getFieldProps('last_name')}
            >
              <Input placeholder={t('steps.corporateIdentity.placeholders.lastName')} />
            </FormItem>

            <FormItem
              label={t('steps.corporateIdentity.labels.secondLastName')}
              field="second_last_name"
              {...getFieldProps('second_last_name')}
            >
              <Input placeholder={t('steps.corporateIdentity.placeholders.secondLastName')} />
            </FormItem>
          </div>

          <div className="form-row">
            <FormItem
              label={t('steps.corporateIdentity.labels.dateOfBirth')}
              field="date_of_birth"
              rules={[
                { required: true, message: t('steps.corporateIdentity.validation.dobRequired') },
                {
                  validator: (value, cb) => {
                    if (!validateAge(value)) {
                      cb(t('steps.corporateIdentity.validation.ageMinimum'));
                    } else {
                      cb();
                    }
                  }
                }
              ]}
              {...getFieldProps('date_of_birth')}
            >
              <DatePicker
                style={{ width: '100%' }}
                placeholder={t('steps.corporateIdentity.placeholders.dateOfBirth')}
                format="DD/MM/YYYY"
              />
            </FormItem>

            <FormItem
              label={t('steps.corporateIdentity.labels.contactEmail')}
              field="contact_email"
              rules={[
                { required: true, message: t('steps.corporateIdentity.validation.emailRequired') },
                { type: 'email', message: t('steps.corporateIdentity.validation.emailInvalid') }
              ]}
              {...getFieldProps('contact_email')}
            >
              <Input placeholder={t('steps.corporateIdentity.placeholders.contactEmail')} />
            </FormItem>
          </div>

          <div className="form-row">
            <FormItem
              label={t('steps.corporateIdentity.labels.idType')}
              field="government_id_type"
              initialValue="CURP"
              {...getFieldProps('government_id_type')}
            >
              <Select>
                <Option value="CURP">{t('steps.identity.options.curp')}</Option>
                <Option value="INE">{t('steps.identity.options.ine')}</Option>
                <Option value="PASSPORT">{t('steps.identity.options.passport')}</Option>
                <Option value="OTHER">{t('steps.identity.options.otherIdType')}</Option>
              </Select>
            </FormItem>

            <FormItem
              label={t('steps.corporateIdentity.labels.idNumber')}
              field="government_id"
              rules={[
                { required: true, message: t('steps.corporateIdentity.validation.idRequired') },
                {
                  validator: (value, cb) => {
                    const idType = form.getFieldValue('government_id_type');
                    if (idType === 'CURP' && !validateCURP(value)) {
                      cb(t('steps.corporateIdentity.validation.curpInvalid'));
                    } else {
                      cb();
                    }
                  }
                }
              ]}
              {...getFieldProps('government_id')}
            >
              <Input
                placeholder={
                  governmentIdType === 'CURP'
                    ? t('steps.identity.placeholders.curpExample')
                    : governmentIdType === 'INE'
                      ? t('steps.identity.placeholders.ineExample')
                      : governmentIdType === 'PASSPORT'
                        ? t('steps.identity.placeholders.passportExample')
                        : t('steps.identity.placeholders.otherIdNumber')
                }
                style={
                  governmentIdType === 'CURP'
                    ? { textTransform: 'uppercase' }
                    : undefined
                }
              />
            </FormItem>
          </div>

          {governmentIdType === 'OTHER' && (
            <FormItem
              label={t('steps.corporateIdentity.labels.specifyIdType')}
              field="government_id_type_other"
              rules={[
                { required: true, message: t('steps.corporateIdentity.validation.specifyIdTypeRequired') }
              ]}
              {...getFieldProps('government_id_type_other')}
            >
              <Input placeholder={t('steps.corporateIdentity.placeholders.specifyIdType')} />
            </FormItem>
          )}

          <div className="form-row form-row--phone">
            <FormItem
              label={t('steps.corporateIdentity.labels.phonePrefix')}
              field="phone_country_code"
              initialValue="+52"
              className="phone-prefix"
              {...getFieldProps('phone_country_code')}
            >
              <Select showSearch dropdownRender={renderCountryCodeDropdown}>
                {countryCodeOptions.map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.value} ({option.label})
                  </Option>
                ))}
              </Select>
            </FormItem>

            <FormItem
              label={t('steps.corporateIdentity.labels.phone')}
              field="phone"
              rules={[
                { required: true, message: t('steps.corporateIdentity.validation.phoneRequired') },
                {
                  match: /^[0-9]{7,15}$/,
                  message: t('steps.corporateIdentity.validation.phoneInvalid')
                }
              ]}
              className="phone-number"
              {...getFieldProps('phone')}
            >
              <Input placeholder={t('steps.corporateIdentity.placeholders.phone')} maxLength={15} />
            </FormItem>
          </div>

          <div className="documents-section">
            <h4>{t('steps.corporateIdentity.labels.idDocumentSection')}</h4>
            {renderIdDocumentCard()}
            {renderPowerDocumentCard()}
          </div>
        </div>

        {/* Dirección comercial se captura en el paso 2 */}

        {/* Botones */}
        <FormItem>
          <Space size="large" style={{ width: '100%', justifyContent: 'center' }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={saveMutation.isPending}
              size="large"
            >
              {t('shared.buttons.continue')}
            </Button>
            {previewMode && freeMode && (
              <Button
                size="large"
                onClick={() => onNext(null)}
              >
                {t('shared.buttons.skipValidation')}
              </Button>
            )}
            <Button
              onClick={handleSaveDraft}
              loading={saveMutation.isPending}
              size="large"
              className="onboarding-draft-button"
            >
              {t('shared.buttons.saveDraft')}
            </Button>
            <Button
              onClick={handleResetForm}
              size="large"
            >
              {t('shared.buttons.cleanForm')}
            </Button>
            <ReportProblemButton stepLabel={t('steps.corporateIdentity.title')} previewMode={previewMode} asButton />
          </Space>
        </FormItem>
      </Form>

      <DocumentPreviewModal
        visible={previewVisible}
        onClose={closePreview}
        document={previewDoc}
        title={previewTitle}
      />
    </div>
  );
}

export default CorporateIdentityStep;
